import React, { useState, useEffect } from 'react';
import chroma from 'chroma-js';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Draggable from 'react-draggable';

import { makeSelectorToGeoJsonNamesDict } from './DataPrep';
import '../css/ColormapLegend.css';

import { CategoricalDataHistogram } from './charts/CategoricalDataHistogram';
import { ContinuousDataHistogram } from './charts/ContinuousDataHistogram';
import { maturityClassColors, discreteColormapAbbrevs } from './Map';

import { DataProvider, useDataSetters, useDataState } from '../DataContext';

const ColormapLegend = ({ 
  selectedInsightMetric, 
  selectedInsightMetricName, 
  minMetricValue, 
  maxMetricValue, 
  userGroup, 
  geojsonData,
  geoJsonVineMaturityData,
  geoJsonVineMaturityNoData,
  onHistogramFilterPlantPoints }) => {

  // State management; see DataContext.js
  const {
    showHistogram,
    isMaturityClassClicked
  } = useDataState();

  const {
    setShowHistogram,
  } = useDataSetters();

  // Get human-readable name for printing
  const selectorToGeoJsonNamesDict = makeSelectorToGeoJsonNamesDict(userGroup);
  const reverseLookup = (obj, value) => Object.entries(obj).find(([k, v]) => v === value)?.[0];
  const humanReadableName = reverseLookup(selectorToGeoJsonNamesDict, selectedInsightMetric);

  // Create color scale function
  const colorScale = isMaturityClassClicked ? maturityClassColors : chroma.scale('RdYlBu');

  const getColorFromScale = (value, min, max) => {
    if (typeof colorScale === 'function') {
      const normalizedValue = (value - min) / (max - min);
      return [...colorScale(normalizedValue).rgb(), colorScale(normalizedValue).alpha() * 255];
    } else {
      return maturityClassColors[value] ? [...maturityClassColors[value]] : [0, 0, 0, 0];
    }
  };

  // Function to convert an RGBA array to a CSS-friendly string
  const rgbaToString = (rgba) => `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${rgba[3] / 255})`;

  // Function to create a CSS linear-gradient string for discrete classes
  const createDiscreteGradient = (maturityClassColors) => {
    const colorStops = Object.values(maturityClassColors)
      .map(rgbaToString)
      .map((color, index, array) => {
        const position = (index / array.length) * 100;
        return `${color} ${position}%, ${color} ${(index + 1) / array.length * 100}%`;
      })
      .join(', ');
    return `linear-gradient(to right, ${colorStops})`;
  };

  const discreteGradientString = createDiscreteGradient(maturityClassColors);

  // Define quantile probabilities
  const probs = [0.0, 0.2, 0.4, 0.6, 0.8, 1.0];

  // Calculate percentiles and corresponding colors
  const percentiles = probs.map(p => minMetricValue + (p * (maxMetricValue - minMetricValue)));
  const colors = percentiles.map(value => getColorFromScale(value, minMetricValue, maxMetricValue));

  // Convert colors to CSS rgba strings
  const colorStrings = colors.map(color => `rgba(${color.join(',')})`);

  // Create the gradient string
  const linearGradientString = `linear-gradient(to right, ${colorStrings.join(',')})`;

  const toggleHistogram = () => {
    setShowHistogram(!showHistogram);
  };

  // Generate labels dynamically for discrete color map
  const discreteColorLabels = isMaturityClassClicked ? (
    Object.keys(maturityClassColors).map((key, index, array) => {
      const position = (index / array.length) * 100 + (0.5 / array.length * 100);
      const abbreviatedText = discreteColormapAbbrevs[key] || key;
      return (
        <div
          className="colormap-label absolute-colormap-label"
          key={index}
          style={{ left: `calc(${position}% - 50%)` }}
        >
          {abbreviatedText}
        </div>
      );
    })
  ) : null;

  return (
    <Draggable handle=".colormap-gradient-container">
      <div className="outer-container">
        <div className="colormap-container">
          <div className="colormap-legend">
            <button 
              className="toggle-button" 
              onClick={toggleHistogram} 
              onTouchStart={toggleHistogram}
              style={{ background: 'transparent', outline: 'none', border: 'none' }}
            >
              {showHistogram ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </button>
          
            {showHistogram && (
              isMaturityClassClicked ? (
                <CategoricalDataHistogram 
                  geojson={geoJsonVineMaturityData} 
                  geojson_nodata={geoJsonVineMaturityNoData}
                  maturityClassColors={maturityClassColors}
                />
              ) : (
                <ContinuousDataHistogram 
                  geojson={geojsonData} 
                  columnKey={selectedInsightMetricName}
                  colorScale={colorScale}
                  minMetricValue={minMetricValue}
                  maxMetricValue={maxMetricValue}
                  onHistogramFilterPlantPoints={onHistogramFilterPlantPoints}
                />
              )
            )}
              { isMaturityClassClicked ? (
                <>
                  <div className="colormap-gradient-container">
                    <div
                      className="colormap-gradient"
                      style={{
                        background: discreteGradientString,
                      }}
                    />
                    {discreteColorLabels}
                  </div>
                  <h3 className="colormap-title">Plant Asset Class</h3>
                </>
              ) :
              (
                <>
                  <div className="colormap-gradient-container">
                    <div
                      className="colormap-gradient"
                      style={{
                        background: isMaturityClassClicked
                          ? discreteGradientString
                          : linearGradientString,
                      }}
                    />
                  </div>
                  <div className="colormap-labels">
                    {percentiles.map((value, index) => (
                      <div key={index} className="colormap-label" style={{ left: `${probs[index] * 100}%` }}>
                        {value.toFixed(1)}
                      </div>
                    ))}
                  </div>
                  <h3 className="colormap-title">{humanReadableName}</h3>
                </>
              )
            }
          </div>
        </div>
      </div>
      
    </Draggable>
    
  );
};

export default ColormapLegend;
